<template>
  <Dialog
    v-model:visible="visibleProxy"
    modal
    append-to="body"
    :closable="false"
    :draggable="false"
    class="constraint-form-dialog w-full"
    :pt="dialogPt"
    data-testid="constraints-form-dialog"
    @show="handleShow"
    @hide="handleHide"
  >
    <template #header>
      <div class="constraint-form-dialog__header">
        <div class="constraint-form-dialog__header-text">
          <h2 id="constraint-form-dialog-title" class="constraint-form-dialog__title">
            {{ dialogTitle }}
          </h2>
          <p v-if="projectName" class="constraint-form-dialog__subtitle">
            {{ projectName }}
          </p>
        </div>
        <button
          type="button"
          class="constraint-form-dialog__close"
          :aria-label="$t('ProjectManagement.cancel')"
          data-testid="constraint-form-close"
          @click="close"
        >
          <i class="pi pi-times" aria-hidden="true" />
        </button>
      </div>
    </template>

    <div class="constraint-form-dialog__body">
      <div class="constraint-form-dialog__field">
        <label class="constraint-form-dialog__label" for="constraint-form-title">
          {{ $t('UserProjects.constraintDialog.titleLabel') }}
          <span class="constraint-form-dialog__required">*</span>
        </label>
        <InputText
          id="constraint-form-title"
          v-model="form.title"
          class="constraint-form-dialog__input w-full"
          :placeholder="$t('UserProjects.constraintDialog.titlePlaceholder')"
          :invalid="!!formErrors.title"
          data-testid="constraint-form-title"
        />
        <small v-if="formErrors.title" class="constraint-form-dialog__error">
          {{ formErrors.title }}
        </small>
      </div>

      <div class="constraint-form-dialog__field">
        <label class="constraint-form-dialog__label" for="constraint-form-category">
          {{ $t('UserProjects.constraintDialog.categoryLabel') }}
          <span class="constraint-form-dialog__required">*</span>
        </label>
        <Select
          input-id="constraint-form-category"
          v-model="form.category"
          :options="categoryOptions"
          option-label="label"
          option-value="value"
          :placeholder="$t('UserProjects.constraintDialog.categoryPlaceholder')"
          class="constraint-form-dialog__select w-full"
          :invalid="!!formErrors.category"
          data-testid="constraint-form-category"
        />
        <small v-if="formErrors.category" class="constraint-form-dialog__error">
          {{ formErrors.category }}
        </small>
      </div>

      <div class="constraint-form-dialog__field">
        <span class="constraint-form-dialog__label">
          {{ $t('UserProjects.constraintDialog.typeLabel') }}
          <span class="constraint-form-dialog__required">*</span>
        </span>
        <div class="constraint-form-dialog__type-grid">
          <button
            type="button"
            class="constraint-form-dialog__type-card"
            :class="{ 'constraint-form-dialog__type-card--active': form.type === CONSTRAINT_FORM_TYPE_INTERN }"
            data-testid="constraint-form-type-intern"
            @click="form.type = CONSTRAINT_FORM_TYPE_INTERN"
          >
            <p class="constraint-form-dialog__type-title">
              {{ $t('UserProjects.constraintDialog.typeInternTitle') }}
            </p>
            <p class="constraint-form-dialog__type-hint">
              {{ $t('UserProjects.constraintDialog.typeInternHint') }}
            </p>
          </button>
          <button
            type="button"
            class="constraint-form-dialog__type-card"
            :class="{ 'constraint-form-dialog__type-card--active': form.type === CONSTRAINT_FORM_TYPE_CROSS }"
            data-testid="constraint-form-type-cross"
            @click="form.type = CONSTRAINT_FORM_TYPE_CROSS"
          >
            <p class="constraint-form-dialog__type-title">
              {{ $t('UserProjects.constraintDialog.typeCrossTitle') }}
            </p>
            <p class="constraint-form-dialog__type-hint">
              {{ $t('UserProjects.constraintDialog.typeCrossHint') }}
            </p>
          </button>
        </div>
        <small v-if="formErrors.type" class="constraint-form-dialog__error">
          {{ formErrors.type }}
        </small>
      </div>

      <div class="constraint-form-dialog__field">
        <label class="constraint-form-dialog__label" for="constraint-form-priority">
          {{ $t('UserProjects.constraintDialog.priorityLabel') }}
          <span class="constraint-form-dialog__required">*</span>
        </label>
        <Select
          input-id="constraint-form-priority"
          v-model="form.priority"
          :options="priorityOptions"
          option-label="label"
          option-value="value"
          class="constraint-form-dialog__select w-full"
          :invalid="!!formErrors.priority"
          data-testid="constraint-form-priority"
        />
        <small v-if="formErrors.priority" class="constraint-form-dialog__error">
          {{ formErrors.priority }}
        </small>
      </div>

      <div class="constraint-form-dialog__field">
        <label class="constraint-form-dialog__label" for="constraint-form-description">
          {{ $t('UserProjects.constraintDialog.descriptionLabel') }}
        </label>
        <Textarea
          id="constraint-form-description"
          v-model="form.description"
          class="constraint-form-dialog__textarea w-full"
          rows="3"
          auto-resize
          :invalid="!!formErrors.description"
          data-testid="constraint-form-description"
          :placeholder="$t('UserProjects.constraintDialog.descriptionPlaceholder')"
          @input="clampDescription"
        />
        <div class="constraint-form-dialog__counter-row">
          <small v-if="formErrors.description" class="constraint-form-dialog__error">
            {{ formErrors.description }}
          </small>
          <span class="constraint-form-dialog__counter">{{ descriptionLength }}/500</span>
        </div>
      </div>

      <div class="constraint-form-dialog__field">
        <label class="constraint-form-dialog__label" for="constraint-form-milestone">
          {{ $t('UserProjects.constraintDialog.milestoneLabel') }}
        </label>
        <Select
          input-id="constraint-form-milestone"
          v-model="form.milestoneId"
          :options="milestoneOptions"
          option-label="label"
          option-value="value"
          option-disabled="disabled"
          :placeholder="$t('UserProjects.constraintDialog.milestonePlaceholder')"
          class="constraint-form-dialog__select w-full"
          show-clear
          data-testid="constraint-form-milestone"
        />
      </div>

      <div class="constraint-form-dialog__field">
        <label class="constraint-form-dialog__label" for="constraint-form-owner">
          {{ $t('UserProjects.constraintDialog.ownerLabel') }}
        </label>
        <UserSelectInput
          v-model="form.ownerUserId"
          :users="participantUsers"
          :placeholder="$t('UserProjects.constraintDialog.ownerPlaceholder')"
          data-testid="constraint-form-owner"
          :invalid="!!formErrors.ownerUserId"
        />
        <small v-if="formErrors.ownerUserId" class="constraint-form-dialog__error">
          {{ formErrors.ownerUserId }}
        </small>
      </div>
    </div>

    <template #footer>
      <div class="constraint-form-dialog__footer">
        <button
          type="button"
          class="constraint-form-dialog__btn-cancel"
          data-testid="constraint-form-cancel"
          @click="close"
        >
          {{ $t('ProjectManagement.cancel') }}
        </button>
        <button
          type="button"
          class="constraint-form-dialog__btn-submit"
          :class="{ 'constraint-form-dialog__btn-submit--active': canSubmit }"
          :disabled="!canSubmit || saving"
          data-testid="constraint-form-submit"
          @click="submit"
        >
          <i v-if="saving" class="pi pi-spin pi-spinner" aria-hidden="true" />
          <span>{{ submitLabel }}</span>
        </button>
      </div>
    </template>
  </Dialog>
</template>

<script>
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import Select from 'primevue/select';
import UserSelectInput from '@/components/global/UserSelectInput.vue';
import {
  CONSTRAINT_FORM_CATEGORIES,
  CONSTRAINT_FORM_PRIORITIES,
  CONSTRAINT_FORM_TYPE_CROSS,
  CONSTRAINT_FORM_TYPE_INTERN,
  emptyConstraintDialogForm,
  mapConstraintApiToDialog,
  mapConstraintDialogToApiPayload,
  validateConstraintDialogForm,
} from '@/utils/constraintFormMapping';

export default {
  name: 'ConstraintFormDialog',
  components: {
    Dialog,
    InputText,
    Textarea,
    Select,
    UserSelectInput,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'create',
      validator: (value) => ['create', 'edit'].includes(value),
    },
    initialConstraint: {
      type: Object,
      default: null,
    },
    projectName: {
      type: String,
      default: '',
    },
    milestones: {
      type: Array,
      default: () => [],
    },
    participantUsers: {
      type: Array,
      default: () => [],
    },
    defaultOwnerUserId: {
      type: String,
      default: '',
    },
    saving: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:visible', 'submit'],
  data() {
    return {
      form: emptyConstraintDialogForm(),
      formErrors: {},
      CONSTRAINT_FORM_TYPE_INTERN,
      CONSTRAINT_FORM_TYPE_CROSS,
    };
  },
  computed: {
    visibleProxy: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit('update:visible', value);
      },
    },
    dialogTitle() {
      return this.mode === 'create'
        ? this.$t('UserProjects.constraintDialog.createTitle')
        : this.$t('UserProjects.constraintDialog.editTitle');
    },
    submitLabel() {
      return this.mode === 'create'
        ? this.$t('UserProjects.constraintDialog.createSubmit')
        : this.$t('UserProjects.constraintDialog.saveSubmit');
    },
    canSubmit() {
      return Boolean(
        String(this.form.title || '').trim()
        && this.form.category
        && this.form.type
        && this.form.priority
        && String(this.form.ownerUserId || '').trim(),
      );
    },
    descriptionLength() {
      return String(this.form.description || '').length;
    },
    categoryOptions() {
      return CONSTRAINT_FORM_CATEGORIES.map((value) => ({
        value,
        label: this.$t(`UserProjects.constraintDialog.categories.${value}`),
      }));
    },
    priorityOptions() {
      return CONSTRAINT_FORM_PRIORITIES.map((value) => ({
        value,
        label: this.$t(`UserProjects.constraintDialog.priorities.${value}`),
      }));
    },
    milestoneOptions() {
      return [
        { value: '', label: this.$t('UserProjects.constraintDialog.milestonePlaceholder'), disabled: false },
        ...(this.milestones || []).map((item) => ({
          value: item.id,
          label: item.title,
          disabled: item.status === 'completed',
        })),
      ];
    },
    dialogPt() {
      return {
        root: {
          'data-testid': 'constraints-form-dialog',
          'aria-labelledby': 'constraint-form-dialog-title',
        },
      };
    },
  },
  watch: {
    initialConstraint: {
      immediate: true,
      handler(nextConstraint) {
        if (this.visible && nextConstraint) {
          this.resetFormFromConstraint(nextConstraint);
        }
      },
    },
  },
  methods: {
    handleShow() {
      this.resetFormFromConstraint(this.initialConstraint);
    },
    handleHide() {
      this.formErrors = {};
    },
    resetFormFromConstraint(constraint) {
      if (constraint) {
        this.form = mapConstraintApiToDialog(constraint);
      } else {
        this.form = {
          ...emptyConstraintDialogForm(),
          ownerUserId: this.defaultOwnerUserId || '',
        };
      }
      this.formErrors = {};
    },
    clampDescription() {
      if (this.form.description.length > 500) {
        this.form.description = this.form.description.slice(0, 500);
      }
    },
    close() {
      this.visibleProxy = false;
    },
    submit() {
      const errors = validateConstraintDialogForm(this.form, this.$t.bind(this));
      this.formErrors = errors;
      if (Object.keys(errors).length > 0) return;
      this.$emit('submit', mapConstraintDialogToApiPayload(this.form));
    },
  },
};
</script>

<style scoped>
.constraint-form-dialog__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
}

.constraint-form-dialog__title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  line-height: 1.3;
  color: var(--color-dark);
}

.constraint-form-dialog__subtitle {
  margin: 0.125rem 0 0;
  font-size: 0.8125rem;
  color: var(--color-disabled);
}

.constraint-form-dialog__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: none;
  border-radius: 0.5rem;
  background: transparent;
  color: var(--color-disabled);
  cursor: pointer;
}

.constraint-form-dialog__close:hover {
  color: var(--color-dark);
}

.constraint-form-dialog__body {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.constraint-form-dialog__field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.constraint-form-dialog__label {
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--color-dark);
}

.constraint-form-dialog__required {
  color: var(--color-error);
}

.constraint-form-dialog__error {
  color: var(--color-error);
  font-size: 0.75rem;
}

.constraint-form-dialog__type-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.constraint-form-dialog__type-card {
  text-align: left;
  padding: 0.75rem;
  border: 1px solid #e5e5e5;
  border-radius: 0.625rem;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}

.constraint-form-dialog__type-card--active {
  border: 2px solid var(--color-orange);
  background: #fdf0e6;
}

.constraint-form-dialog__type-title {
  margin: 0;
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--color-dark);
}

.constraint-form-dialog__type-hint {
  margin: 0.125rem 0 0;
  font-size: 0.6875rem;
  line-height: 1.35;
  color: #6b6b6b;
}

.constraint-form-dialog__counter-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.constraint-form-dialog__counter {
  margin-left: auto;
  font-size: 0.6875rem;
  color: var(--color-disabled);
}

.constraint-form-dialog__footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  width: 100%;
}

.constraint-form-dialog__btn-cancel {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 1rem;
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: 0.625rem;
  background: var(--color-white);
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-dark);
  cursor: pointer;
}

.constraint-form-dialog__btn-cancel:hover {
  background: var(--color-light-grey);
}

.constraint-form-dialog__btn-submit {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1.25rem;
  border: none;
  border-radius: 0.625rem;
  background: var(--color-inactive);
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-white);
  cursor: not-allowed;
}

.constraint-form-dialog__btn-submit--active:not(:disabled) {
  background: var(--accent-gradient);
  cursor: pointer;
}
</style>

<style>
.constraint-form-dialog.p-dialog {
  display: flex;
  flex-direction: column;
  width: min(540px, calc(100vw - 2rem));
  max-height: min(90vh, 44rem);
  margin: 1rem;
  border: none;
  border-radius: 1.25rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}

.constraint-form-dialog.p-dialog .p-dialog-header {
  flex-shrink: 0;
  padding: 1.5rem 1.5rem 1rem;
  border-bottom: none;
}

.constraint-form-dialog.p-dialog .p-dialog-content {
  flex: 1 1 auto;
  min-height: 0;
  padding: 0 1.5rem 1.5rem;
  overflow-y: auto;
}

.constraint-form-dialog.p-dialog .p-dialog-footer {
  flex-shrink: 0;
  padding: 0 1.5rem 1.5rem;
  border-top: none;
}

.constraint-form-dialog .constraint-form-dialog__input.p-inputtext,
.constraint-form-dialog .constraint-form-dialog__select.p-select,
.constraint-form-dialog .constraint-form-dialog__textarea.p-textarea {
  min-height: 2.5rem;
  border-radius: 0.625rem;
  font-size: 0.875rem;
}

.constraint-form-dialog .constraint-form-dialog__input.p-inputtext:enabled:focus,
.constraint-form-dialog .constraint-form-dialog__textarea.p-textarea:enabled:focus {
  border-color: var(--color-orange);
  box-shadow: 0 0 0 3px rgba(228, 130, 26, 0.15);
}

.constraint-form-dialog .p-dialog-mask {
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(2px);
}
</style>
